import React, { useMemo } from 'react';
import { getCardLoc, getCardTypeName, getUILoc } from '../utils/cardUtils.js';
import { CardIllustration } from './Card.js';

/**
 * 関連カードの抽出ロジック（CardDetailと共有）
 */
export const useRelatedCards = (card, cardDefs) => {
    return useMemo(() => {
        if (!card || !cardDefs) return [];
        const baseDef = (Array.isArray(cardDefs) ? cardDefs.find(d => d.id === card.id) : cardDefs[card.id]) || card;
        const ids = new Set();
        if (Array.isArray(baseDef.related_card_templates)) baseDef.related_card_templates.forEach(id => ids.add(id));
        const findCardTemplates = (obj) => {
            if (!obj || typeof obj !== 'object') return;
            if (obj.card_template_id) ids.add(obj.card_template_id);
            if (obj.card_if_above) ids.add(obj.card_if_above);
            if (obj.card_if_below) ids.add(obj.card_if_below);
            if (obj.options && Array.isArray(obj.options)) obj.options.forEach(id => ids.add(id));
            Object.values(obj).forEach(value => {
                if (Array.isArray(value)) value.forEach(item => findCardTemplates(item));
                else if (typeof value === 'object') findCardTemplates(value);
            });
        };
        if (baseDef.triggers) findCardTemplates(baseDef.triggers);
        return Array.from(ids).map(id => {
            const def = Array.isArray(cardDefs) ? cardDefs.find(d => d.id === parseInt(id)) : cardDefs[id];
            if (!def) return null;
            const loc = getCardLoc(id);
            return { 
                ...def, 
                name: loc.name, 
                description: loc.description,
                typeName: getCardTypeName(def.card_type)
            };
        }).filter(Boolean);
    }, [card, cardDefs]);
};

/**
 * CardDetail と構造を同一化したホバープレビューコンポーネント
 */
const CardPreview = ({ card, cardDefs }) => {
  const relatedCards = useRelatedCards(card, cardDefs);

  if (!card) return null;
  const cardLoc = getCardLoc(card.id);

  return (
    <div className="card-detail-modal" style={{ pointerEvents: 'auto', zIndex: 2000 }}>
        <CardIllustration cardId={card.id} isBackground={true} />

        <div className="card-detail-header">
            <div className="card-header-main">
                <h2>{cardLoc.name}</h2>
                {card.is_token && <span className="token-badge">TOKEN</span>}
            </div>
        </div>

        <div className="card-detail-content">
            <div className="card-detail-info">
                <div className="info-row">
                    <span className="info-label">{getUILoc('LABELS.CARD_TYPE')}:</span>
                    <span className="info-value">{getCardTypeName(card.card_type)}</span>
                </div>
                <div className="info-row">
                    <span className="info-label">{getUILoc('LABELS.REQUIRED_SCALE')}:</span>
                    <span className="info-value">{card.required_scale}</span>
                </div>
                {card.card_type === 1 && (
                    <div className="info-row">
                        <span className="info-label">{getUILoc('LABELS.DURABILITY')}:</span>
                        <span className="info-value">
                            {card.current_durability !== undefined ? `${card.current_durability} / ${card.durability}` : card.durability}
                        </span>
                    </div>
                )}
            </div>
            
            <div className="card-detail-description">
                <h4>{getUILoc('LABELS.EFFECT')}:</h4>
                <p>{cardLoc.description}</p>
            </div>

            {relatedCards.length > 0 && (
                <div className="related-cards-section">
                    <h4>{getUILoc('LABELS.RELATED_CARDS')}:</h4>
                    {relatedCards.map((rc, idx) => (
                        <div key={idx} className="related-card-item">
                            <div className="related-card-header">
                                <CardIllustration cardId={rc.id} isMini={true} />
                                <div className="related-card-title-group">
                                    <span className="related-card-name">{rc.name}</span>
                                    <span className="related-card-type">{rc.typeName}</span>
                                    {rc.is_token && <span className="token-badge">TOKEN</span>}
                                </div>
                            </div>
                            <p className="related-card-description">{rc.description}</p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    </div>
  );
};

export default CardPreview;
